import React from 'react';
import { motion } from 'framer-motion';
import { Lang } from '../types';
interface HowItWorksProps {
  lang: Lang;
}
const content = {
  en: {
    title: 'How Booking Works',
    subtitle: '(Simple & Clear)',
    steps: [{
      num: '01',
      title: 'Send your idea',
      desc: 'DM or fill the form below with your concept, placement, and size'
    }, {
      num: '02',
      title: 'Get your quote',
      desc: "I'll review and reply within 24–48h with a price range and plan"
    }, {
      num: '03',
      title: 'Secure with deposit',
      desc: 'Lock in your spot at Piajas Ink with a deposit'
    }, {
      num: '04',
      title: 'Show up & get tattooed',
      desc: 'We review the final design together and start your session'
    }],
    locationTitle: '📍 Piajas Ink Tattoo Studio',
    locationDesc: '2116 SW 8th St, Miami FL 33135'
  },
  es: {
    title: 'Cómo funciona la reserva',
    subtitle: '(simple y claro)',
    steps: [{
      num: '01',
      title: 'Envías tu idea',
      desc: 'DM o llena el formulario con tu concepto, zona y tamaño'
    }, {
      num: '02',
      title: 'Recibes tu cotización',
      desc: 'Reviso y respondo en 24–48h con rango de precio y plan'
    }, {
      num: '03',
      title: 'Aseguras con depósito',
      desc: 'Confirmas tu cupo en Piajas Ink con un depósito'
    }, {
      num: '04',
      title: 'Llegas y te tatúas',
      desc: 'Revisamos el diseño final juntos y arrancamos la sesión'
    }],
    locationTitle: '📍 Piajas Ink Tattoo Studio',
    locationDesc: '2116 SW 8th St, Miami FL 33135'
  }
};
export function HowItWorksSection({
  lang
}: HowItWorksProps) {
  const t = content[lang];
  return <section id="how-it-works" className="py-12 md:py-16 bg-studio-bgAlt border-y border-studio-border" data-id="element-230">
      <div className="max-w-3xl mx-auto px-6" data-id="element-231">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="text-center mb-16" data-id="element-232">
          <h2 className="text-3xl md:text-5xl font-bold mb-2" data-id="element-233">{t.title}</h2>
          <p className="text-studio-muted font-playfair italic text-xl" data-id="element-234">
            {t.subtitle}
          </p>
        </motion.div>

        {/* Mobile: Simple stacked cards */}
        <div className="md:hidden space-y-6" data-id="element-235">
          {t.steps.map((step, i) => <motion.div key={step.num} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: i * 0.1
        }} className="bg-studio-bg border border-studio-border p-5 rounded-sm" data-id="element-236">
              <div className="flex items-center gap-4 mb-3" data-id="element-237">
                <div className="w-10 h-10 shrink-0 rounded-full bg-studio-bg border border-studio-accent text-studio-accent flex items-center justify-center font-playfair font-bold text-base" data-id="element-238">
                  {step.num}
                </div>
                <h3 className="text-lg font-bold text-studio-text" data-id="element-239">
                  {step.title}
                </h3>
              </div>
              <p className="text-studio-muted text-sm leading-relaxed pl-14" dangerouslySetInnerHTML={{
            __html: step.desc.replace('24–48h', '<strong class="text-studio-text">24–48h</strong>')
          }} data-id="element-240" />
            </motion.div>)}
        </div>

        {/* Desktop: Timeline layout */}
        <div className="relative hidden md:block" data-id="element-241">
          {/* Vertical Line */}
          <div className="absolute left-1/2 top-0 bottom-0 w-px bg-studio-border -translate-x-1/2" data-id="element-242" />

          <div className="space-y-12" data-id="element-243">
            {t.steps.map((step, i) => <motion.div key={step.num} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-100px'
          }} transition={{
            delay: i * 0.1
          }} className={`relative flex items-center gap-8 ${i % 2 === 0 ? 'flex-row-reverse' : ''}`} data-id="element-244">
                {/* Number Node */}
                <div className="absolute left-1/2 -translate-x-1/2 w-14 h-14 rounded-full bg-studio-bg border border-studio-accent text-studio-accent flex items-center justify-center font-playfair font-bold text-xl z-10" data-id="element-245">
                  {step.num}
                </div>

                {/* Content */}
                <div className={`w-1/2 ${i % 2 === 0 ? 'pl-16' : 'pr-16 text-right'}`} data-id="element-246">
                  <div className="bg-studio-bg border border-studio-border p-6 hover:border-studio-accent/50 transition-colors duration-300" data-id="element-247">
                    <h3 className="text-xl font-bold mb-2 text-studio-text" data-id="element-248">
                      {step.title}
                    </h3>
                    <p className="text-studio-muted" dangerouslySetInnerHTML={{
                  __html: step.desc.replace('24–48h', '<strong class="text-studio-text">24–48h</strong>')
                }} data-id="element-249" />
                  </div>
                </div>
              </motion.div>)}
          </div>
        </div>

        <motion.div initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true
      }} className="mt-20 text-center border border-studio-border p-8 bg-studio-bg" data-id="element-250">
          <h4 className="font-playfair font-bold text-xl text-studio-accent mb-2" data-id="element-251">
            {t.locationTitle}
          </h4>
          <p className="text-studio-muted" data-id="element-252">{t.locationDesc}</p>
        </motion.div>
      </div>
    </section>;
}